/* ==========================================================================
   FLC Forge — Collection Filter & Sort
   ========================================================================== */

// Use the section's actual ID (from the template JSON) so Shopify's Sections
// Rendering API returns the saved settings rather than schema defaults.
const collectionSectionId =
  document.querySelector('.main-collection')?.dataset.sectionId || 'main';

class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer    = this.querySelector('[data-filter-inner]');
    this.openBtn   = document.querySelector('[data-filter-open]');
    this.closeBtn  = this.querySelector('[data-filter-close]');
    this.clearBtn  = this.querySelector('[data-filter-clear]');
    this.applyBtn  = this.querySelector('[data-filter-apply]');
    this.form      = this.querySelector('[data-filter-form]') || document.querySelector('[data-filter-form]');
    this.summary   = document.querySelector('[data-active-filters]');

    this.openBtn?.addEventListener('click',  () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());

    // Clear: uncheck all inputs then fetch the unfiltered collection
    this.clearBtn?.addEventListener('click', () => this.clearFilters());

    // Apply: submit current selections then close
    this.applyBtn?.addEventListener('click', async () => {
      await this.submitFilter();
      this.close();
    });

    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Auto-apply while drawer is open so the count updates in real time
    this.form?.addEventListener('change', FLC.debounce(() => this.submitFilter(), 400));
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    FLC.openOverlay();
    FLC.trapFocus(this);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    FLC.closeOverlay();
  }

  clearFilters() {
    // Uncheck every checkbox in the form
    this.form?.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    // Clear price range inputs so they are omitted from the params
    this.form?.querySelectorAll('input[type="number"]').forEach(n => { n.value = ''; });
    this.submitFilter();
  }

  async submitFilter() {
    const params = new URLSearchParams(new FormData(this.form));
    params.set('sort_by', 'manual');
    const url = `${window.location.pathname}?${params.toString()}`;
    this.setLoading(true);

    try {
      const res  = await fetch(`${url}&sections=${collectionSectionId}`);
      const data = await res.json();

      if (data[collectionSectionId]) {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(data[collectionSectionId], 'text/html');
        this.replaceSection(doc);
        this.updateURL(url);
      }
    } catch (err) {
      console.error('Filter failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  replaceSection(doc) {
    replaceCollectionContent(doc);
    syncSortSelect('manual');
    this.summary = document.querySelector('[data-active-filters]');
  }

  updateURL(url) {
    window.history.pushState({}, '', url);
  }

  setLoading(state) {
    document.querySelector('[data-product-grid]')?.classList.toggle('is-loading', state);
  }
}

customElements.define('filter-drawer', FilterDrawer);

/* Sidebar filter — auto-apply on change (static/sidebar mode)
   ========================================================================== */
function replaceCollectionContent(doc) {
  const newHeader     = doc.querySelector('.collection-header');
  const newGrid       = doc.querySelector('[data-product-grid]');
  const newFilters    = doc.querySelector('[data-active-filters]');
  const newCount      = doc.querySelector('[data-product-count]');
  const newPagination = doc.querySelector('[data-pagination-wrapper]');

  const header = document.querySelector('.collection-header');
  if (header && newHeader) header.replaceWith(newHeader);

  const grid = document.querySelector('[data-product-grid]');
  if (grid && newGrid) grid.replaceWith(newGrid);

  const count = document.querySelector('[data-product-count]');
  if (count && newCount) count.replaceWith(newCount);

  const active = document.querySelector('[data-active-filters]');
  if (active && newFilters) {
    active.replaceWith(newFilters);
  } else if (active && !newFilters) {
    active.remove();
  } else if (!active && newFilters) {
    document.querySelector('.collection-toolbar')?.insertAdjacentElement('afterend', newFilters);
  }

  // Pagination / infinite-scroll trigger must be re-rendered for the filtered
  // result set — otherwise stale links point at the wrong page or drop the
  // active filter params entirely.
  const pagination = document.querySelector('[data-pagination-wrapper]');
  if (pagination && newPagination) {
    pagination.replaceWith(newPagination);
  } else if (pagination && !newPagination) {
    pagination.innerHTML = '';
  }
}

// Keep the sort dropdown's displayed value in sync with the sort actually
// applied, since filtering forces manual/curated order behind the scenes.
function syncSortSelect(value) {
  const select = document.querySelector('[data-sort-select]');
  if (select && [...select.options].some(o => o.value === value)) {
    select.value = value;
  }
}

const sidebarForm = document.querySelector('#filter-form-sidebar');
if (sidebarForm) {
  sidebarForm.addEventListener('change', FLC.debounce(async () => {
    const params = new URLSearchParams(new FormData(sidebarForm));
    params.set('sort_by', 'manual');
    const url = `${window.location.pathname}?${params.toString()}`;
    document.querySelector('[data-product-grid]')?.classList.add('is-loading');
    try {
      const res  = await fetch(`${url}&sections=${collectionSectionId}`);
      const data = await res.json();
      if (data[collectionSectionId]) {
        const doc      = new DOMParser().parseFromString(data[collectionSectionId], 'text/html');
        replaceCollectionContent(doc);
        syncSortSelect('manual');
        window.history.pushState({}, '', url);
      }
    } catch (err) {
      console.error('Sidebar filter failed:', err);
    } finally {
      document.querySelector('[data-product-grid]')?.classList.remove('is-loading');
    }
  }, 400));
}

/* Sort
   ========================================================================== */
const sortSelect = document.querySelector('[data-sort-select]');
if (sortSelect) {
  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

/* Active filter removal
   ========================================================================== */
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('[data-remove-filter]');
  if (!removeBtn) return;
  e.preventDefault();

  const url = new URL(removeBtn.href);
  window.location.href = url.toString();
});

/* Infinite scroll
   ========================================================================== */
class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl;
    if (!this.nextUrl) return;

    this.grid    = document.querySelector('[data-product-grid]');
    this.trigger = this;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.nextUrl) this.loadMore();
    }, { rootMargin: '200px' });

    observer.observe(this.trigger);
  }

  async loadMore() {
    const url = this.nextUrl;
    this.nextUrl = null; // prevent double loading
    this.classList.add('is-loading');

    try {
      const res  = await fetch(`${url}&sections=${collectionSectionId}`);
      const data = await res.json();

      if (data[collectionSectionId]) {
        const parser   = new DOMParser();
        const doc      = parser.parseFromString(data[collectionSectionId], 'text/html');
        const newItems = doc.querySelectorAll('[data-product-card]');
        newItems.forEach(item => this.grid?.appendChild(item));

        const nextTrigger = doc.querySelector('infinite-scroll');
        this.nextUrl = nextTrigger?.dataset.nextUrl || null;
      }
    } catch (err) {
      console.error('Infinite scroll failed:', err);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
